import {
  contractRegistry,
  ContractDeployment,
  ContractAddresses,
  DeploymentVersion
} from './contractRegistry.ts'

export type ResolveMode = 'create' | 'query'

const ALL_CHAIN_IDS = Array.from(
  new Set(contractRegistry.map(entry => entry.chainId))
).sort((a, b) => a - b)

const registryByChain = contractRegistry.reduce(
  (acc, deployment) => {
    const list = acc.get(deployment.chainId) ?? []
    list.push(deployment)
    acc.set(deployment.chainId, list)
    return acc
  },
  new Map<number, ContractDeployment[]>()
)

const getEntriesForChain = (chainId?: number): ContractDeployment[] => {
  if (chainId == null) return []
  return registryByChain.get(Number(chainId)) ?? []
}

const getWritableEntry = (chainId?: number): ContractDeployment | undefined =>
  getEntriesForChain(chainId).find(entry => entry.canCreate)

const getPrimaryEntry = (chainId?: number): ContractDeployment | undefined => {
  const writable = getWritableEntry(chainId)
  if (writable) return writable
  const entries = getEntriesForChain(chainId)
  return entries.length ? entries[0] : undefined
}

const addressFrom = <K extends keyof ContractAddresses>(
  entry: ContractDeployment | undefined,
  key: K
) => {
  if (!entry) return ''
  const value = entry.addresses[key]
  if (!value) return ''
  return typeof value === 'string' ? value : ''
}

export const resolveContracts = (
  chainId: number,
  mode: ResolveMode = 'query'
): ContractDeployment[] | ContractDeployment | undefined => {
  if (mode === 'create') {
    return getWritableEntry(chainId)
  }
  return getEntriesForChain(chainId)
}

export const getFactoryAddress = (chainId?: number) =>
  addressFrom(getWritableEntry(chainId), 'factoryAddress')

export const getMulticallAddress = (chainId?: number) =>
  addressFrom(getPrimaryEntry(chainId), 'multicallAddress')

export const getInfoAddress = (chainId?: number) =>
  addressFrom(getPrimaryEntry(chainId), 'infoAddress')

export const getHotKeyAddress = (chainId?: number) =>
  addressFrom(getPrimaryEntry(chainId), 'hotKeyAddress')

export const getWethAddress = (chainId?: number) =>
  addressFrom(getPrimaryEntry(chainId), 'WETHAddress')

export const getDefaultAddress = (chainId?: number) =>
  addressFrom(getPrimaryEntry(chainId), 'defaultAddress')

export const getRouterAddress = (chainId: number) => {
  const entry = getWritableEntry(chainId)
  return entry?.addresses.routerAddress ?? {}
}

export type CreatePoolOption = {
  id: string
  chainId: number
  label: string
  version: DeploymentVersion
  factoryAddress: string
  routerAddress: string
  infoAddress?: string
  hotKeyAddress?: string
  description: string
}

export const getCreatePoolOptions = (chainId?: number): CreatePoolOption[] => {
  const entries = getEntriesForChain(chainId).filter(entry => entry.canCreate)
  const options: CreatePoolOption[] = []
  entries.forEach(entry => {
    const routerMap = entry.addresses.routerAddress ?? {}
    const labels = Object.keys(routerMap)
    if (labels.length === 0) {
      options.push({
        id: `${entry.id}:default`,
        chainId: entry.chainId,
        label: 'default',
        version: entry.version,
        factoryAddress: entry.addresses.factoryAddress ?? '',
        routerAddress: '',
        infoAddress: entry.addresses.infoAddress,
        hotKeyAddress: entry.addresses.hotKeyAddress,
        description: entry.description
      })
      return
    }
    labels.forEach(label => {
      options.push({
        id: `${entry.id}:${label}`,
        chainId: entry.chainId,
        label,
        version: entry.version,
        factoryAddress: entry.addresses.factoryAddress ?? '',
        routerAddress: routerMap[label] ?? '',
        infoAddress: entry.addresses.infoAddress,
        hotKeyAddress: entry.addresses.hotKeyAddress,
        description: entry.description
      })
    })
  })
  return options
}

export const getAllMulticallAddresses = (chainId: number) =>
  getEntriesForChain(chainId).map(entry => ({
    id: entry.id,
    description: entry.description,
    type: entry.version,
    versionType: entry.version,
    canCreate: entry.canCreate,
    chainId: entry.chainId,
    ...entry.addresses
  }))

export const getPrimaryDeployment = (chainId?: number) =>
  getPrimaryEntry(chainId)

export const getDeploymentsForChain = (chainId?: number) =>
  getEntriesForChain(chainId).slice()

export const getKnownChainIds = () => ALL_CHAIN_IDS.slice()
